import React, { useRef, useState } from 'react';
import Tooltip from '../ui/Tooltip';
import ContextMenu from '../ui/ContextMenu';
import {
  TASK_STATUSES,
  STATUS_LABELS,
  taskStatusOf,
  statusPatch,
} from '../../extensions/bundled/task-manager/taskStatus';
import './TaskStatus.css';

// The click-catching wrapper of a portalled menu takes no place in the row:
// as a real box it joined the row's flex gap and shifted the row by 10 px
// whenever the menu opened (owner, 13.10.2026).
const MENU_WRAP = { display: 'contents' };

/**
 * Task status, Linear-style (card ef074711, owner 01.10.2026):
 *   ○ To do (grey) · ◐ In progress (orange) · ✓ Done (green) · ⊘ Dismissed (red).
 * Drawn as SVG so all four share one 16px box and stroke, and colour is never
 * the only channel — each shape differs too.
 */
export function TaskStatusIcon({ status, size = 15 }) {
  const s = TASK_STATUSES.includes(status) ? status : 'todo';
  return (
    <svg
      className={`task-status-icon task-status-icon--${s}`}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      aria-hidden
      focusable="false"
    >
      {s === 'todo' && <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" />}
      {s === 'in_progress' && (
        <>
          <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M8 3.75 A4.25 4.25 0 0 1 8 12.25 Z" fill="currentColor" />
        </>
      )}
      {/* Done is an outlined check (Boards Redesign artboards), the same ring
          as To do with a tick inside — not a filled disc. */}
      {s === 'done' && (
        <>
          <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M5.75 8.1 7.4 9.75 10.4 6.6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}
      {s === 'dismissed' && (
        <>
          <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M3.6 12.4 12.4 3.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}

/** The status options for a picker (TaskDetailModal's CompactSelectPicker). */
export const STATUS_OPTIONS = TASK_STATUSES.map((value) => ({
  value,
  label: STATUS_LABELS[value],
  leading: <TaskStatusIcon status={value} size={14} />,
}));

/**
 * The status control on a card and a list row: the icon; a click opens the
 * four statuses (Linear, GitHub Projects). Replaces the done checkbox.
 */
export function TaskStatusButton({ task, onChange, className }) {
  const status = taskStatusOf(task);
  const ref = useRef(null);
  const [menu, setMenu] = useState(null);
  const stop = (e) => e.stopPropagation();

  const open = (e) => {
    e.stopPropagation();
    const r = ref.current?.getBoundingClientRect();
    setMenu(r ? { x: r.left, y: r.bottom + 4 } : { x: e.clientX, y: e.clientY });
  };

  return (
    <>
      <Tooltip content={`Status: ${STATUS_LABELS[status]}`} side="top">
        <button
          ref={ref}
          type="button"
          className={`${className || ''} task-status-btn`.trim()}
          aria-label={`Status: ${STATUS_LABELS[status]}. Change status`}
          aria-haspopup="menu"
          aria-expanded={!!menu}
          data-status={status}
          onPointerDown={stop}
          onClick={open}
        >
          <TaskStatusIcon status={status} />
        </button>
      </Tooltip>
      {/* The menu is portalled, but React still bubbles its clicks through
          THIS tree — into the card title (which starts a rename) and the
          card's drag handle. Stopped here. */}
      {menu && (
        <span style={MENU_WRAP} onClick={stop} onPointerDown={stop} onMouseDown={stop}>
          <ContextMenu
            x={menu.x}
            y={menu.y}
            className="task-status-menu"
            onClose={() => setMenu(null)}
            items={TASK_STATUSES.map((s) => ({
              icon: <TaskStatusIcon status={s} size={14} />,
              label: STATUS_LABELS[s],
              checked: s === status,
              onClick: () => {
                setMenu(null);
                if (s !== status) onChange(statusPatch(s));
              },
            }))}
          />
        </span>
      )}
    </>
  );
}
